/**
 * Page stats. (Brief §3.4 et §3.5)
 */

import { useEffect, useState } from "react";
import type { Projection } from "@rm/shared";

import { api } from "../lib/api.js";
import { BarChart, LineChart } from "../components/Chart.js";
import { Card, Empty, Spinner, Stat } from "../components/ui.js";

interface DayRow {
  day: string;
  followers: number | null;
  followers_delta: number | null;
  impressions_sum: number | null;
  profile_clicks_sum: number | null;
  replies_count: number;
  points: number;
  streak_length: number;
}

interface StatsResponse {
  days: DayRow[];
  projection: Projection;
  personal_best: {
    best_week_points: number;
    best_week_followers: number;
    best_day_impressions: number;
    best_streak: number;
  };
  budget: { spentUsd: number; capUsd: number; spentEur: number; capEur: number; ratio: number };
  attribution: { active_days_avg: number; off_days_avg: number; sample: number };
}

export function StatsPage() {
  const [data, setData] = useState<StatsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<StatsResponse>("/stats?days=60")
      .then(setData)
      .catch((err) => setError(err instanceof Error ? err.message : "Chargement impossible"));
  }, []);

  if (error) return <Empty title="Stats indisponibles" hint={error} />;
  if (!data) return <Spinner />;

  if (data.days.length === 0) {
    return (
      <Empty
        title="Aucune donnée"
        hint="Le tracker tourne chaque nuit à 3 h 15. Tu peux le lancer à la main depuis les réglages."
      />
    );
  }

  const { projection, personal_best: best, attribution } = data;

  return (
    <div className="flex flex-col gap-4 px-4 pt-4">
      <Card>
        <h2 className="mb-3 text-sm font-medium">Route to 1000</h2>
        {projection.targetDate ? (
          <div className="flex items-end justify-between">
            <Stat
              label="Arrivée estimée"
              value={formatDate(projection.targetDate)}
              hint={`${projection.daysToTarget} jours au rythme actuel`}
            />
            <Stat
              label="Vélocité"
              value={`${projection.velocityPerDay > 0 ? "+" : ""}${projection.velocityPerDay}`}
              hint="followers / jour"
            />
          </div>
        ) : (
          <p className="text-sm text-[var(--color-muted)]">
            {projection.current >= 1000
              ? "Objectif atteint."
              : "Croissance nulle ou négative sur la fenêtre : pas de projection."}
          </p>
        )}
        <p className="mt-2 text-xs text-[var(--color-muted)] tabular-nums">
          {projection.current} followers · régression sur {projection.sampleSize} jours
        </p>
      </Card>

      <Card>
        <LineChart
          label="Followers"
          points={data.days.map((d) => ({ day: d.day, value: d.followers }))}
        />
      </Card>

      <Card>
        <BarChart
          label="Replies / jour"
          points={data.days.map((d) => ({ day: d.day, value: d.replies_count }))}
          color="var(--color-good)"
        />
      </Card>

      <Card>
        <LineChart
          label="Impressions / jour"
          points={data.days.map((d) => ({ day: d.day, value: d.impressions_sum }))}
          color="var(--color-warn)"
        />
      </Card>

      <Card>
        <BarChart
          label="Clics profil / jour (proxy « visites du profil »)"
          points={data.days.map((d) => ({ day: d.day, value: d.profile_clicks_sum }))}
          color="var(--color-muted)"
        />
        <p className="mt-2 text-[11px] text-[var(--color-muted)]">
          L'API X n'expose pas la statistique globale de visites du profil. Cet agrégat de
          <code className="mx-1">user_profile_clicks</code> en tient lieu : il ne compte que les clics
          venus de mes 15 derniers tweets.
        </p>
      </Card>

      <Card>
        <h2 className="mb-3 text-sm font-medium">Personal best</h2>
        <div className="grid grid-cols-2 gap-4">
          <Stat label="Meilleure semaine" value={best.best_week_points} hint="points" />
          <Stat label="Meilleur gain hebdo" value={`+${best.best_week_followers}`} hint="followers" />
          <Stat label="Meilleur jour" value={best.best_day_impressions.toLocaleString("fr-FR")} hint="impressions" />
          <Stat label="Plus longue streak" value={best.best_streak} hint="jours" />
        </div>
      </Card>

      <Card>
        <h2 className="mb-2 text-sm font-medium">Attribution grossière</h2>
        <div className="grid grid-cols-2 gap-4">
          <Stat
            label="Jours actifs"
            value={`${attribution.active_days_avg >= 0 ? "+" : ""}${attribution.active_days_avg}`}
            hint="followers / jour"
          />
          <Stat
            label="Jours off"
            value={`${attribution.off_days_avg >= 0 ? "+" : ""}${attribution.off_days_avg}`}
            hint="followers / jour"
          />
        </div>
        <p className="mt-2 text-[11px] text-[var(--color-muted)]">
          Corrélation, pas causalité — sur {attribution.sample} jours observés. Un « jour actif » est
          un jour avec au moins une reply, la veille ou le jour même.
        </p>
      </Card>
    </div>
  );
}

function formatDate(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}
